<div class="store-ranking-chart-container">
  <!-- Loading State -->
  <div class="loading-overlay" *ngIf="rankingsLoading">
    <div class="loading-spinner"></div>
    <span>Loading rankings...</span>
  </div>

  <!-- No Data State -->
  <div class="no-data-message" *ngIf="!rankingsLoading && !hasData">
    <span>No ranking data available</span>
  </div>

  <!-- Download Button — sits in the right padding zone, always rendered -->
  <button
    class="download-button"
    (click)="downloadScatterData()"
    [disabled]="rankingsLoading || !hasData || pdfGenerating"
    title="Download as PDF">
    <svg *ngIf="pdfGenerating" class="btn-spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-dasharray="40 20" stroke-linecap="round"/>
    </svg>
    <svg *ngIf="!pdfGenerating" width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4.66669 6.66669L8.00002 10L11.3334 6.66669" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M8 10V2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </button>

  <!-- Chart Content -->
  <div class="chart-content" *ngIf="!rankingsLoading && hasData">
    <div class="y-axis-label">
      <span>Store Rank</span>
    </div>
    <div class="chart-area">
      <!-- Y-axis line -->
      <div class="y-axis-line"></div>
      <div class="chart-plot-area">
        <!-- Vertical grid lines -->
        <div class="vertical-grid-lines">
          <ng-container *ngFor="let label of xAxisLabels; let i = index">
            <div class="grid-line"
                 *ngIf="!(i === 0 && (label === '$0' || label === '0' || label === '$0K' || label === '$0M'))"
                 [style.left.%]="(i / (xAxisLabels.length - 1)) * 100"></div>
          </ng-container>
        </div>

        <!-- Competitor scatter points (only visible ones within zoom range) -->
        <div
          class="scatter-point"
          *ngFor="let point of visibleDataPoints; trackBy: trackById"
          [ngStyle]="getScatterPointStyle(point)"
          (mouseenter)="onPointHover(point, $event)"
          (mouseleave)="onPointLeave()"
          (click)="onPointClick(point, $event)">
        </div>

        <!-- Your store marker -->
        <div class="your-store-marker" [ngStyle]="getYourStoreStyle()" *ngIf="yourStorePosition.rank && isYourStoreVisible">
          <div class="store-circle"
               (mouseenter)="onYourStoreHover($event)"
               (mouseleave)="onYourStoreLeave()"
               (click)="onYourStoreClick($event)">
            <svg width="28" height="28" viewBox="0 0 29 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.268 1.40137C21.7662 1.40137 27.8451 7.4803 27.8452 14.9785C27.8452 22.4768 21.7663 28.5557 14.268 28.5557C6.76979 28.5556 0.690857 22.4767 0.690857 14.9785C0.690912 7.48034 6.76983 1.40142 14.268 1.40137Z" fill="#26C066" stroke="white" stroke-width="1.18059"/>
            </svg>
          </div>
          <span class="store-label" [ngClass]="getYourStoreLabelClass()">{{ getYourStoreLabel() }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="x-axis-wrapper" *ngIf="!rankingsLoading && hasData">
    <div class="x-axis-spacer"></div>
    <div class="x-axis">
      <div class="x-axis-line"></div>
      <div class="x-axis-numbers">
        <span class="axis-label" *ngFor="let label of xAxisLabels">{{ label }}</span>
      </div>
      <div class="x-axis-title">{{ getXAxisTitle() }}</div>

    <!-- Zoom: editable inputs + range slider -->
    <div class="zoom-control-row">
      <div class="zoom-input-group">
        <label class="zoom-input-label">Min</label>
        <input
          #minInput
          class="zoom-input"
          type="text"
          (keypress)="allowOnlyNumbers($event)"
          (focus)="onZoomInputFocus('min', minInput)"
          (input)="onZoomInputChange('min', minInput)"
          (blur)="onZoomInputCommit('min', minInput)"
          (keydown.enter)="minInput.blur()" />
      </div>

      <div class="zoom-slider-wrap">
        <ngx-slider
          class="scatter-zoom-slider"
          [(value)]="zoomMin"
          [(highValue)]="zoomMax"
          [options]="sliderOptions"
          (userChangeEnd)="onSliderChange($event)">
        </ngx-slider>
      </div>

      <div class="zoom-input-group">
        <label class="zoom-input-label">Max</label>
        <div class="zoom-input-with-reset">
          <input
            #maxInput
            class="zoom-input"
            type="text"
            (keypress)="allowOnlyNumbers($event)"
            (focus)="onZoomInputFocus('max', maxInput)"
            (input)="onZoomInputChange('max', maxInput)"
            (blur)="onZoomInputCommit('max', maxInput)"
            (keydown.enter)="maxInput.blur()" />
          <button class="zoom-reset-link" *ngIf="isZoomActive" (click)="resetZoom()">Reset</button>
        </div>
      </div>
    </div>
    </div>
  </div>

  <!-- Tooltips positioned outside overflow container for proper z-index layering -->
  <!-- Competitor Tooltip -->
  <div
    class="your-store-tooltip"
    *ngIf="hoveredPoint"
    [ngStyle]="getTooltipStyle()">
    <div class="tooltip-header">
      <span class="store-name">{{ hoveredPoint.storeName }}</span>
      <span class="store-number" *ngIf="hoveredPoint?.storeNumber">#{{ hoveredPoint.storeNumber }}</span>
    </div>
    <div class="tooltip-content">
      <div class="tooltip-row">
        <span class="label">Fit</span>
        <span class="value">{{ hoveredPoint?.fit ? formatFitType(hoveredPoint.fit) : 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Status</span>
        <span class="value">{{ hoveredPoint?.status || 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Sales per Sq Ft</span>
        <span class="value">{{ hoveredPoint?.sqft ? '$' + formatNumber(hoveredPoint.sqft) : 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Total Sales Area</span>
        <span class="value">{{ hoveredPoint?.salesArea ? formatNumber(hoveredPoint.salesArea) + ' sq ft' : 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Total Sales Volume</span>
        <span class="value sales-volume">{{ hoveredPoint?.value ? '$' + formatNumber(hoveredPoint.value) : 'N/A' }}</span>
      </div>
    </div>
    <div class="tooltip-arrow" [ngClass]="{'tooltip-arrow-flipped': isTooltipFlipped}" [ngStyle]="getTooltipArrowStyle()">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="14" viewBox="0 0 24 14" fill="none">
        <path d="M21.8046 1.92835e-05C23.2825 1.99579e-05 24.0226 1.78687 22.9776 2.83191L12.9064 12.9031C12.2586 13.5509 11.2082 13.5509 10.5604 12.9031L0.489236 2.8319C-0.555799 1.78687 0.184338 1.92835e-05 1.66224 2.01264e-05L21.8046 1.92835e-05Z" fill="white"/>
      </svg>
    </div>
  </div>

  <!-- Your Store Tooltip -->
  <div
    class="your-store-tooltip"
    *ngIf="showYourStoreTooltip"
    [ngStyle]="getYourStoreTooltipStyle()">
    <div class="tooltip-header">
      <span class="store-name">{{ store?.name || yourStorePosition.name }}</span>
      <span class="store-number" *ngIf="yourStorePosition?.storeNumber">#{{ yourStorePosition.storeNumber }}</span>
    </div>
    <div class="tooltip-content">
      <div class="tooltip-row">
        <span class="label">Fit</span>
        <span class="value">{{ yourStorePosition?.fit ? formatFitType(yourStorePosition.fit) : 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Status:</span>
        <span class="value">{{ yourStorePosition?.status || 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Sales per Sq Ft</span>
        <span class="value">{{ yourStorePosition?.sqft ? '$' + formatNumber(yourStorePosition.sqft) : 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Total Sales Area</span>
        <span class="value">{{ yourStorePosition?.salesArea ? formatNumber(yourStorePosition.salesArea) + ' sq ft' : 'N/A' }}</span>
      </div>
      <div class="tooltip-row">
        <span class="label">Total Sales Volume</span>
        <span class="value sales-volume">{{ yourStorePosition?.value ? '$' + formatNumber(yourStorePosition.value) : 'N/A' }}</span>
      </div>
    </div>
    <div class="tooltip-arrow" [ngClass]="{'tooltip-arrow-flipped': isYourStoreTooltipFlipped}" [ngStyle]="getYourStoreTooltipArrowStyle()">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="14" viewBox="0 0 24 14" fill="none">
        <path d="M21.8046 1.92835e-05C23.2825 1.99579e-05 24.0226 1.78687 22.9776 2.83191L12.9064 12.9031C12.2586 13.5509 11.2082 13.5509 10.5604 12.9031L0.489236 2.8319C-0.555799 1.78687 0.184338 1.92835e-05 1.66224 2.01264e-05L21.8046 1.92835e-05Z" fill="white"/>
      </svg>
    </div>
  </div>
</div>
